import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    Typography,
    IconButton,
    Button,
    Input,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
    Spinner,
    Alert,
    Textarea
} from "@material-tailwind/react";
import {
    HandThumbUpIcon,
    HandThumbDownIcon,
    XMarkIcon,
    ClockIcon,
    UserGroupIcon,
    FireIcon,
    HeartIcon,
    ShareIcon,
    TrashIcon,
    SpeakerWaveIcon,
    ChatBubbleLeftRightIcon,
    ArrowDownTrayIcon,
    ShoppingCartIcon
} from "@heroicons/react/24/outline";
import { generateRecipePdf } from '../utils/generateRecipePdf';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';
import { resolveImageUrl as resolveImg } from '../utils/imageUrl';
import { isMaisterchefGermanTheme } from '../utils/branding';
import { useNavigate } from 'react-router-dom';

const RecipeDetail = ({ recipe, isOpen, onClose, imageData }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [likes, setLikes] = useState(0);
    const [dislikes, setDislikes] = useState(0);
    const [isLiked, setIsLiked] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertColor, setAlertColor] = useState('black');
    const [audioBase64, setAudioBase64] = useState(null);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const [winePairings, setWinePairings] = useState([]);
    const [loadingWine, setLoadingWine] = useState(false);
    const [showWinePairings, setShowWinePairings] = useState(false);
    const [localRecipeId, setLocalRecipeId] = useState(null);

    const effectiveRecipeId = localRecipeId || recipe?.recipe_id;
    const safeRecipe = {
        ...recipe,
        recipe_id: effectiveRecipeId,
        ingredients: recipe?.ingredients || [],
        instructions: recipe?.instructions || '',
        name: recipe?.name || recipe?.strMeal || 'Unknown Recipe',
        intro: recipe?.intro || '',
        nutritions: recipe?.nutritions || {},
        calories_per_serving: recipe?.calories_per_serving || 0,
        cooking_time: recipe?.cooking_time || recipe?.strInstructions || '',
        servings: recipe?.servings || 1
    };

    const userId = localStorage.getItem('userId');


    const { language } = useLanguage();

    // Map befr to french and benl to nederlands for translations
    let translationLanguage = language;
    if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
    if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
    if (language === 'bh-ar' || language === 'bahrain-arabic') translationLanguage = 'arabic';
    if (language === 'bh-en' || language === 'bahrain-english') translationLanguage = 'english';
    if (language === 'uae-ar' || language === 'uae-arabic') translationLanguage = 'arabic';
    if (language === 'uae-en' || language === 'uae-english') translationLanguage = 'english';
    if (language === 'sa-ar' || language === 'sa-arabic') translationLanguage = 'arabic';
    if (language === 'sa-en' || language === 'sa-english') translationLanguage = 'english';
    if (language === 'kw-ar' || language === 'kw-arabic') translationLanguage = 'arabic';
    if (language === 'kw-en' || language === 'kw-english') translationLanguage = 'english';
    if (language === 'qa-ar' || language === 'qa-arabic') translationLanguage = 'arabic';
    if (language === 'qa-en' || language === 'qa-english') translationLanguage = 'english';
    if (language === 'om-ar' || language === 'om-arabic') translationLanguage = 'arabic';
    if (language === 'om-en' || language === 'om-english') translationLanguage = 'english';
    if (language === 'iq-ar' || language === 'iq-arabic') translationLanguage = 'arabic';
    if (language === 'iq-en' || language === 'iq-english') translationLanguage = 'english';
    if (language === 'jo-ar' || language === 'jo-arabic') translationLanguage = 'arabic';
    if (language === 'jo-en' || language === 'jo-english') translationLanguage = 'english';
    if (language === 'lb-ar' || language === 'lb-arabic') translationLanguage = 'arabic';
    if (language === 'lb-en' || language === 'lb-english') translationLanguage = 'english';
    if (language === 'sy-ar' || language === 'sy-arabic') translationLanguage = 'arabic';
    if (language === 'sy-en' || language === 'sy-english') translationLanguage = 'english';
    if (language === 'ps-ar' || language === 'ps-arabic') translationLanguage = 'arabic';
    if (language === 'ps-en' || language === 'ps-english') translationLanguage = 'english';
    if (language === 'ye-ar' || language === 'ye-arabic') translationLanguage = 'arabic';
    if (language === 'ye-en' || language === 'ye-english') translationLanguage = 'english';
    if (language === 'eg-ar' || language === 'eg-arabic') translationLanguage = 'arabic';
    if (language === 'eg-en' || language === 'eg-english') translationLanguage = 'english';
    if (language === 'pl-pl') translationLanguage = 'polish';
    if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';

    const t = languages[translationLanguage] || languages['english']; // Get translations with fallback to English
    const navigate = useNavigate();  // Initialize useNavigate

    useEffect(() => {
        setLocalRecipeId(recipe?.recipe_id || null);
        setWinePairings([]);
        setShowWinePairings(false);
    }, [recipe]);

    // Fetch comments when the recipe is loaded
    useEffect(() => {
        if (effectiveRecipeId) {
            const fetchComments = async () => {
                try {
                    const response = await axios.get(`/api/comments/${effectiveRecipeId}`);
                    setComments(response.data);
                } catch (error) {
                    console.error("Error fetching comments:", error);
                }
            };
            fetchComments();
        } else {
            setComments([]);
        }
    }, [effectiveRecipeId]);

    // Fetch the initial count of likes and dislikes
    useEffect(() => {
        if (effectiveRecipeId) {
            const fetchLikeData = async () => {
                try {
                    const response = await axios.get(`/api/recipe-likes/${effectiveRecipeId}`);
                    setLikes(response.data.likes);
                    setDislikes(response.data.dislikes);
                } catch (error) {
                    console.error('Error fetching like data:', error);
                }
            };
            fetchLikeData();
        }
    }, [effectiveRecipeId]);

    useEffect(() => {
        const url = resolveImg(imageData)
            || resolveImg(recipe?.image_path)
            || resolveImg(recipe?.original_image_url);
        if (url) setImageUrl(url);
    }, [imageData, recipe?.image_path, recipe?.original_image_url]);

    const handleLikeDislike = async (likeStatus) => {
        try {
            const response = await axios.post('/api/like-dislike-recipe', {
                userId: userId, // Hardcoded for now
                recipeId: safeRecipe.recipe_id,
                isLike: likeStatus,
            });

            if (response.data.success) {
                setLikes(response.data.likes);
                setDislikes(response.data.dislikes);
                setIsLiked(likeStatus);
            }
        } catch (error) {
            console.error('Error updating like/dislike:', error);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            const response = await axios.delete(`/api/comments/${commentId}`, {
                data: { user_id: userId }, // Stuur het ingelogde user_id mee in de body
            });
            if (response.data.success) {
                // Verwijder de comment uit de huidige lijst
                setComments(comments.filter(comment => comment.comment_id !== commentId));
            }
        } catch (error) {
            console.error("Error deleting comment:", error);
        }
    };

    const handleGetAudio = async () => {
        try {
            setLoadingAudio(true);

            const response = await axios.post('/api/ai/tts', {
                language,
                intro: safeRecipe.intro,
                instructions: safeRecipe.instructions,
            }, { timeout: 60000 });

            const { audio } = response.data;

            if (audio) {
                setAudioBase64(`data:audio/mpeg;base64,${audio}`);
                setAlertMessage(t.audioFetched || 'Audio is ready to play.');
                setAlertColor('green');
                setShowAlert(true);
                setTimeout(() => setShowAlert(false), 3000);
            } else {
                throw new Error("No audio data received.");
            }
        } catch (error) {
            console.error("Error fetching audio:", error);
            setAlertMessage(t.errorFetchingAudio || 'Error fetching audio. Please try again.');
            setAlertColor('red');
            setShowAlert(true);
        } finally {
            setLoadingAudio(false);
        }
    };

    const handleWinePairing = async () => {
        if (winePairings.length > 0) {
            setShowWinePairings(!showWinePairings);
            return;
        }
        try {
            setLoadingWine(true);
            const response = await axios.post('/api/ai/wine-pairing', {
                recipe: {
                    name: safeRecipe.name,
                    ingredients: safeRecipe.ingredients,
                    intro: safeRecipe.intro,
                },
                language,
            }, { timeout: 30000 });
            setWinePairings(response.data.pairings || []);
            setShowWinePairings(true);
        } catch (error) {
            console.error("Error fetching wine pairings:", error);
            setAlertMessage(t.errorWinePairing || 'Could not get wine pairings. Please try again.');
            setAlertColor('red');
            setShowAlert(true);
        } finally {
            setLoadingWine(false);
        }
    };

    const handleFavorite = async () => {

        try {
            const recipeData = {
                userId: userId,
                name: safeRecipe.name,
                intro: safeRecipe.intro,
                ingredients: safeRecipe.ingredients || [],
                instructions: safeRecipe.instructions,
                allergens: safeRecipe.allergens || [],
                nutritions: safeRecipe.nutritions,
                calories_per_serving: safeRecipe.calories_per_serving,
                cooking_time: safeRecipe.cooking_time,
                servings: safeRecipe.servings,
                imageData: imageData || imageUrl || safeRecipe.image_path || null,
                language: language,
            };

            const response = await axios.post('/api/save-recipe', recipeData);

            if (response.data.success) {
                if (response.data.recipeId) {
                    setLocalRecipeId(response.data.recipeId);
                }
                setAlertMessage(t.recipeSaved || 'Recipe saved to favorites.');
                setAlertColor('green');
            } else {
                throw new Error(response.data.error || t.errorSavingRecipe || 'Failed to save recipe');
            }
        } catch (error) {
            if (error.response && error.response.status === 409) {
                setAlertMessage(t.recipeAlreadySaved || 'Recipe is already saved.');
            } else {
                setAlertMessage(t.errorSavingRecipe || 'Error saving safeRecipe. Please try again.');
            }
            setAlertColor('black');
        }

        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
    };

    const handleShare = async (platform) => {
        const recipeText = `${safeRecipe.name}\n\n${safeRecipe.intro || ''}`;
        const shareUrl = window.location.href;

        try {
            switch (platform) {
                case 'WhatsApp':
                    window.open(`https://wa.me/?text=${encodeURIComponent(recipeText + '\n' + shareUrl)}`, '_blank');
                    break;
                case 'Facebook':
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(recipeText)}`, '_blank');
                    break;
                case 'Twitter':
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(recipeText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
                    break;
                case 'Email':
                    window.open(`mailto:?subject=${encodeURIComponent(safeRecipe.name)}&body=${encodeURIComponent(recipeText + '\n\n' + shareUrl)}`, '_blank');
                    break;
                default:
                    if (navigator.share) {
                        await navigator.share({ title: safeRecipe.name, text: recipeText, url: shareUrl });
                    } else {
                        await navigator.clipboard.writeText(recipeText + '\n' + shareUrl);
                    }
            }
            setAlertMessage(`${t.sharedOn || 'Recipe shared on'} ${platform}.`);
            setAlertColor('green');
        } catch (error) {
            if (error.name !== 'AbortError') {
                try {
                    await navigator.clipboard.writeText(recipeText + '\n' + shareUrl);
                    setAlertMessage(t.copiedToClipboard || 'Recipe copied to clipboard!');
                    setAlertColor('green');
                } catch {
                    setAlertMessage(t.shareError || 'Could not share recipe.');
                    setAlertColor('red');
                }
            }
        }
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
    };

    const handleCommentSubmit = async () => {
        if (!newComment.trim()) return;

        try {
            const response = await axios.post('/api/comments', {
                recipe_id: safeRecipe.recipe_id,
                user_id: userId,
                content: newComment,
            });
            if (response.data.success) {
                setNewComment("");
                const updatedComments = await axios.get(`/api/comments/${safeRecipe.recipe_id}`);
                setComments(updatedComments.data);
            }
        } catch (error) {
            console.error(t.errorSubmittingComment || "Error submitting comment:", error);
        }
    };

    // Cleanup audio when the dialog is closed
    useEffect(() => {
        if (!isOpen) {
            setAudioBase64(null);
        }
    }, [isOpen]);

    const handleAskQuestion = () => {
        // Store the recipe data in localStorage
        const tempAskRecipe = {
            recipe: safeRecipe,
            imageData: imageData
        };
        localStorage.setItem('tempAskRecipe', JSON.stringify(tempAskRecipe));

        // Navigate to the chat screen
        navigate('/chat');  // Assuming '/chat' is the route for your ChatScreen
        onClose();  // Close the RecipeDetail dialog
    };

    const handleDownloadPdf = async () => {
        try {
            await generateRecipePdf(safeRecipe, t, imageData);
            setAlertMessage(t.pdfDownloaded || 'PDF downloaded successfully!');
            setAlertColor('green');
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setAlertMessage(t.errorDownloadingPdf || 'Error downloading PDF.');
            setAlertColor('red');
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
        }
    };

    const addToShoppingList = () => {
        const currentList = JSON.parse(localStorage.getItem('shoppingList') || '[]');
        const newItems = safeRecipe.ingredients ? safeRecipe.ingredients.map(ingredient => ({
            name: ingredient.name || ingredient,
            quantity: ingredient.quantity || '',
            checked: false
        })) : [];
        const updatedList = [...currentList, ...newItems];
        localStorage.setItem('shoppingList', JSON.stringify(updatedList));
        setAlertMessage(t.addedToShoppingList || 'Ingredients added to shopping list!');
        setAlertColor('green');
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
    };


    if (!safeRecipe) return null;

    return (
        <Dialog open={isOpen} handler={onClose} size="lg" className={isMaisterchefGermanTheme(language) ? 'mc-de-dialog' : ''}>
            <DialogHeader className="flex justify-between items-center shadow-sm">
                <span></span>
                <Typography variant='h4' color="blue-gray">{t.recipeDetails || 'Recipe Details'}</Typography>
                <div className="flex gap-2">
                    <IconButton variant="text" color="blue-gray" onClick={onClose}>
                        <XMarkIcon className="h-5 w-5" />
                    </IconButton>
                </div>
            </DialogHeader>
            <DialogBody className="max-h-[80vh] overflow-y-auto p-0 pt-2">
                <div className="relative">
                    {imageUrl ? (
                        <img src={imageUrl} alt={safeRecipe.name} className="w-[96%] block m-auto h-48 object-cover rounded-lg" />
                    ) : (
                        <div className="w-full h-48 bg-gray-200 animate-pulse"></div>
                    )}

                    <div className=" px-4">


                        {/* Audio Button */}
                        <div className="absolute top-1 left-3">
                            <IconButton
                                variant="filled"
                                color="white"
                                className="flex items-center gap-2"
                                onClick={handleGetAudio}
                            >
                                <SpeakerWaveIcon className="h-5 w-5" />
                            </IconButton>
                        </div>

                        {/* Audio Loader */}
                        {loadingAudio && (
                            <div className="mt-4 flex items-center gap-2 justify-center">
                                <Spinner className="h-6 w-6 text-blue-gray-50" />
                                {t.loadingAudio || 'Loading audio...'}
                            </div>
                        )}

                        {/* Audio Player */}
                        {audioBase64 && (
                            <div className="mt-4 w-full">
                                <audio controls className="w-full">
                                    <source src={audioBase64} type="audio/mpeg" />
                                    Your browser does not support the audio element.
                                </audio>
                            </div>
                        )}
                    </div>
                </div>

                <div className="py-6 px-4">
                    <Typography variant='h2' className="text-2xl font-bold">{safeRecipe.name}</Typography>
                    <div className="flex mb-4 gap-2 items-center mt-2 justify-between">
                        {safeRecipe.recipe_id &&
                            <div className="flex gap-2">
                                <Button
                                    color={isLiked === true ? "blue" : "blue-gray"}
                                    variant="outlined"
                                    size='sm'
                                    onClick={() => handleLikeDislike(true)}
                                    className={` shadow-none rounded-full flex items-center gap-2 text-[10px] ${isLiked === true ? 'bg-blue-50 ' : 'bg-gray-50'} border-none`}
                                >
                                    <HandThumbUpIcon className="h-5 w-5" />
                                    <span>{likes}</span>
                                </Button>

                                <Button
                                    color={isLiked === false ? "red" : "blue-gray"}
                                    variant="outlined"
                                    size='sm'
                                    onClick={() => handleLikeDislike(false)}
                                    className={`shadow-none rounded-full flex items-center gap-2 text-[10px] ${isLiked === false ? 'bg-red-50' : 'bg-gray-50'} border-none`}
                                >
                                    <HandThumbDownIcon className="h-5 w-5" />
                                    <span>{dislikes}</span>
                                </Button>
                            </div>
                        }

                        <div className="flex gap-2">

                            <IconButton
                                color="blue"
                                variant="outlined"
                                onClick={handleAskQuestion}
                                className="mr-2 shadow-none rounded-full bg-blue-50 border-none"
                            >
                                <ChatBubbleLeftRightIcon className="h-5 w-5" />
                            </IconButton>

                            <IconButton
                                color="green"
                                variant="outlined"
                                onClick={handleFavorite}
                                className="mr-2 shadow-none rounded-full bg-green-50 border-none"
                            >
                                <HeartIcon className="h-5 w-5" />
                            </IconButton>
                            <IconButton
                                color="orange"
                                variant="outlined"
                                onClick={handleDownloadPdf}
                                className="mr-2 shadow-none rounded-full bg-orange-50 border-none"
                            >
                                <ArrowDownTrayIcon className="h-5 w-5" />
                            </IconButton>
                            <IconButton
                                color="amber"
                                variant="outlined"
                                onClick={addToShoppingList}
                                className="mr-2 shadow-none rounded-full bg-amber-50 border-none"
                            >
                                <ShoppingCartIcon className="h-5 w-5" />
                            </IconButton>
                            <IconButton
                                color="deep-purple"
                                variant="outlined"
                                onClick={handleWinePairing}
                                className="mr-2 shadow-none rounded-full bg-purple-50 border-none"
                                disabled={loadingWine}
                            >
                                {loadingWine ? <Spinner className="h-4 w-4" /> : <span className="text-lg">🍷</span>}
                            </IconButton>
                            <Menu>
                                <MenuHandler>
                                    <IconButton
                                        color="green"
                                        variant="outlined"
                                        className="mr-2 shadow-none rounded-full bg-green-50 border-none"
                                    >
                                        <ShareIcon className="h-5 w-5" />
                                    </IconButton>
                                </MenuHandler>
                                <MenuList className="z-[10000]">
                                    <MenuItem onClick={() => handleShare("WhatsApp")}>WhatsApp</MenuItem>
                                    <MenuItem onClick={() => handleShare("Facebook")}>Facebook</MenuItem>
                                    <MenuItem onClick={() => handleShare("Twitter")}>Twitter</MenuItem>
                                    <MenuItem onClick={() => handleShare("Email")}>Email</MenuItem>
                                </MenuList>
                            </Menu>
                        </div>
                    </div>

                    <div className="flex justify-between mb-6">
                        <div className="flex items-center">
                            <ClockIcon className="h-5 w-5 mr-1 text-blue-500" />
                            <Typography variant='small'>{safeRecipe.cooking_time} {t.minutes || 'minutes'}</Typography>
                        </div> |
                        <div className="flex items-center">
                            <UserGroupIcon className="h-5 w-5 mr-1 text-green-500" />
                            <Typography variant='small'>{safeRecipe.servings} {t.servings || 'servings'}</Typography>
                        </div> |
                        <div className="flex items-center">
                            <FireIcon className="h-5 w-5 mr-1 text-orange-500" />
                            <Typography variant='small'>{safeRecipe.calories_per_serving} {t.calories || 'calories'}</Typography>
                        </div>
                    </div>

                    <Typography variant='paragraph' className="text-gray-600 mb-4">{safeRecipe.intro}</Typography>

                    {showWinePairings && winePairings.length > 0 && (
                        <div className="bg-purple-50 rounded-lg p-4 mb-4">
                            <Typography variant="h5" className="text-purple-900 mb-3 flex items-center gap-2">
                                <span>🍷</span> {t.winePairings || 'Wine Pairings'}
                            </Typography>
                            <div className="space-y-3">
                                {winePairings.map((pairing, idx) => (
                                    <div key={idx} className="bg-white rounded-lg p-3 shadow-sm">
                                        <Typography variant="h6" className="text-purple-800">{pairing.wine}</Typography>
                                        <Typography variant="small" className="text-gray-600">{pairing.grape} · {pairing.region}</Typography>
                                        <Typography variant="small" className="text-gray-700 block mt-1">{pairing.reason}</Typography>
                                        <Typography variant="small" className="text-purple-600 font-medium mt-1">{pairing.price_range}</Typography>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <hr className='my-5' />

                    <Typography variant='h3' className="text-xl font-semibold mb-2">{t.ingredients || 'Ingredients'}</Typography>

                    <div className="grid grid-cols-3 gap-3 mb-6">
                        {safeRecipe.ingredients && safeRecipe.ingredients.map((ingredient, index) => (
                            <div key={index} className="flex justify-center items-center">
                                <div className="bg-orange-50 rounded-lg py-2 h-24 flex flex-col items-center text-center justify-center w-full">
                                    <span className="text-sm text-orange-500 font-bold">{ingredient.name}</span>
                                    <span className="text-xs">{ingredient.quantity}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <hr className='my-5' />

                    <Typography variant='h3' className="text-xl font-semibold mb-2">{t.instructions || 'Instructions'}</Typography>
                    <ol className="list-decimal list-inside space-y-2">
                        {safeRecipe.instructions.map((step, index) => (
                            <li key={index} className="mb-2">{step}</li>
                        ))}
                    </ol>

                    <hr className='my-5' />

                    {safeRecipe.recipe_id && (
                        <div className="mt-6" id="comments-section">
                            <div className="flex items-center gap-2 mb-4">
                                <Typography variant='h5' className="text-xl font-semibold">{t.comments || 'Comments'}</Typography>
                                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-bold">
                                    {comments.length}
                                </span>
                            </div>
                            <div className="mb-6">
                                {comments.length > 0 ? (
                                    comments.map((comment) => (
                                        <div key={comment.comment_id} className="mb-4 p-3 bg-gray-50 rounded-lg flex justify-between items-start">
                                            <div>
                                                <Typography variant='small' className="font-semibold text-green-700">{comment.username}</Typography>
                                                <Typography variant='paragraph' className="mt-1">{comment.content}</Typography>
                                                <Typography variant='small' className="text-gray-500 mt-1">{new Date(comment.created_at).toLocaleString()}</Typography>
                                            </div>
                                            {String(comment.user_id) === String(userId) && (
                                                <IconButton
                                                    color="red"
                                                    variant="text"
                                                    size="sm"
                                                    onClick={() => handleDeleteComment(comment.comment_id)}
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </IconButton>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <Typography variant='small'>{t.noComments || 'No comments yet. Be the first to comment!'}</Typography>
                                )}
                            </div>

                            <div className="flex flex-col gap-4 items-center">
                                <Textarea
                                    label={t.addComment || "Add a comment"}
                                    value={newComment}
                                    rows={10}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    className="w-full"
                                />
                                <Button
                                    className=' w-full items-center'
                                    onClick={handleCommentSubmit}
                                >{t.submit || 'Submit'}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </DialogBody>
            {showAlert && (
                <div className="fixed bottom-4 left-5 right-5 z-50">
                    <Alert
                        open={showAlert}
                        color={alertColor}
                        className="max-w-screen-md mx-auto"
                        animate={{
                            mount: { y: 0 },
                            unmount: { y: 100 },
                        }}
                        action={
                            <IconButton
                                variant="text"
                                color="white"
                                size="sm"
                                className="!absolute top-3 right-3"
                                onClick={() => setShowAlert(false)}
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </IconButton>
                        }
                    >
                        {alertMessage}
                    </Alert>
                </div>
            )}
        </Dialog>
    );
};

export default RecipeDetail;
